import React, { useMemo, useState } from 'react';
import { CallHistoryListProps } from '../types';
import CallStatusCard from './CallStatusCard';

const CallHistoryList: React.FC<CallHistoryListProps> = ({ calls, isLoading = false }) => {
  // Local filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'any' | 'processing' | 'completed' | 'failed'>('any');
  const [halalFilter, setHalalFilter] = useState<'any' | 'all' | 'some' | 'on_request' | 'nearby' | 'none' | 'not_mentioned'>('any');
  const [alcoholFilter, setAlcoholFilter] = useState<'any' | 'alcohol_free' | 'restaurant_only' | 'room_only' | 'none' | 'not_mentioned'>('any');
  const [followUpFilter, setFollowUpFilter] = useState<'any' | 'update_data' | 'call_back' | 'human_needed'>('any');
  const [collapsedByBatch, setCollapsedByBatch] = useState<Record<string, boolean>>({});

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('any');
    setHalalFilter('any');
    setAlcoholFilter('any');
    setFollowUpFilter('any');
    setCurrentPage(1);
  };

  // Sort and filter calls
  const filteredCalls = useMemo(() => {
    const sorted = [...calls].sort((a, b) =>
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    const term = searchTerm.trim().toLowerCase();
    return sorted.filter((c) => {
      const phoneOk = term === '' || c.phoneNumber.toLowerCase().includes(term);
      const statusOk = statusFilter === 'any' || c.status === statusFilter;
      const halalOk = halalFilter === 'any' || (c.results && c.results.halalFood === halalFilter);
      const alcoholOk = alcoholFilter === 'any' || (c.results && c.results.alcoholPolicies === alcoholFilter);
      const followOk = followUpFilter === 'any' || (c.results?.meta?.followUpAction === followUpFilter);
      return phoneOk && statusOk && halalOk && alcoholOk && followOk;
    });
  }, [calls, searchTerm, statusFilter, halalFilter, alcoholFilter, followUpFilter]);

  // Flatten grouped calls for pagination
  const flattenedGroupedCalls = useMemo(() => {
    const result: typeof filteredCalls = [];
    const batches: Record<string, { name: string; items: typeof filteredCalls }> = {} as any;
    const singles: typeof filteredCalls = [] as any;

    for (const c of filteredCalls) {
      if (c.batchId) {
        const key = c.batchId;
        if (!batches[key]) batches[key] = { name: c.batchName || `Batch ${key.slice(0, 8)}`, items: [] as any } as any;
        (batches[key].items as any).push(c);
      } else {
        (singles as any).push(c);
      }
    }

    // Add batch headers and items
    Object.entries(batches).forEach(([key, batch]) => {
      result.push({ ...batch.items[0], _isBatchHeader: true, _batchKey: key, _batchName: batch.name, _batchCount: batch.items.length } as any);
      result.push(...batch.items);
    });

    // Add single calls
    result.push(...singles);

    return result;
  }, [filteredCalls]);

  // Pagination calculations
  const totalItems = flattenedGroupedCalls.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = flattenedGroupedCalls.slice(startIndex, endIndex);

  // Regroup paginated items for display
  const grouped = useMemo(() => {
    const batches: Record<string, { name: string; items: typeof filteredCalls; isHeaderVisible?: boolean }> = {} as any;
    const singles: typeof filteredCalls = [] as any;

    for (const c of paginatedItems) {
      if ((c as any)._isBatchHeader) {
        const key = (c as any)._batchKey;
        batches[key] = {
          name: (c as any)._batchName,
          items: [],
          isHeaderVisible: true
        } as any;
      } else if (c.batchId) {
        const key = c.batchId;
        if (batches[key]) {
          (batches[key].items as any).push(c);
        }
      } else {
        (singles as any).push(c);
      }
    }
    return { batches, singles };
  }, [paginatedItems]);

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  if (isLoading && calls.length === 0) {
    return (
      <div className="call-history-loading">
        <div className="loading-spinner"></div>
        <p>Loading call history...</p>
      </div>
    );
  }

  if (calls.length === 0) {
    return (
      <div className="call-history-empty">
        <div className="empty-state">
          <h3>No verification calls yet</h3>
          <p>Enter a phone number above to initiate your first property verification call.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="call-history">
      <div className="history-header">
        <h2>Call History</h2>
        <span className="call-count">
          {filteredCalls.length !== calls.length
            ? `${filteredCalls.length}/${calls.length} filtered`
            : `${calls.length} total calls`
          }
        </span>
      </div>

      <div className="filters">
        <input
          type="text"
          placeholder="Search phone number"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as any)} className="filter-select">
          <option value="any">Status: Any</option>
          <option value="processing">Processing</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <select value={halalFilter} onChange={(e) => setHalalFilter(e.target.value as any)} className="filter-select">
          <option value="any">Halal: Any</option>
          <option value="all">All</option>
          <option value="some">Some</option>
          <option value="on_request">On request</option>
          <option value="nearby">Nearby only</option>
          <option value="none">None</option>
          <option value="not_mentioned">Not mentioned</option>
        </select>
        <select value={alcoholFilter} onChange={(e) => setAlcoholFilter(e.target.value as any)} className="filter-select">
          <option value="any">Alcohol: Any</option>
          <option value="alcohol_free">Alcohol-free property</option>
          <option value="restaurant_only">Alcohol-free restaurant</option>
          <option value="room_only">Alcohol-free room</option>
          <option value="none">None</option>
          <option value="not_mentioned">Not mentioned</option>
        </select>
        <select value={followUpFilter} onChange={(e) => setFollowUpFilter(e.target.value as any)} className="filter-select">
          <option value="any">Follow-up: Any</option>
          <option value="update_data">Update data</option>
          <option value="call_back">Call back / contact</option>
          <option value="human_needed">Human needed</option>
        </select>
        <button className="clear-btn" onClick={clearFilters}>Clear</button>
      </div>

      <div className="calls-list">
        {Object.entries(grouped.batches).map(([key, group]) => (
          <div className="batch-group" key={key}>
            <div className="batch-header">
              <div className="batch-title">
                <span className="name">{group.name}</span>
                <span className="count">{group.items.length} calls</span>
              </div>
              <button className="batch-toggle" onClick={() => setCollapsedByBatch(prev => ({ ...prev, [key]: !prev[key] }))}>
                {collapsedByBatch[key] ? 'Expand' : 'Collapse'}
              </button>
            </div>
            {!collapsedByBatch[key] && (
              <div className="batch-calls">
                {group.items.map(call => (
                  <CallStatusCard key={call.id} call={call} />
                ))}
              </div>
            )}
          </div>
        ))}

        {grouped.singles.map(call => (
          <CallStatusCard key={call.id} call={call} />
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={goToPrevious}
            disabled={currentPage === 1}
            title="Previous page"
          >
            ← Previous
          </button>

          <div className="pagination-info">
            <span className="page-indicator">
              Page {currentPage} of {totalPages}
            </span>
            <span className="item-count">
              {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} items
            </span>
          </div>

          <button
            className="pagination-btn"
            onClick={goToNext}
            disabled={currentPage === totalPages}
            title="Next page"
          >
            Next →
          </button>
        </div>
      )}

      <div className="export-row">
        <button className="export-btn" onClick={() => {
          const rows = filteredCalls.map(c => ({
            id: c.id,
            phone: c.phoneNumber,
            property: c.propertyName || '',
            status: c.status,
            halal: c.results?.halalFood || '',
            alcohol: c.results?.alcoholPolicies || '',
            modest: c.results?.leisureFacilities.modestSwimwear || '',
            women_only: c.results?.leisureFacilities.womenOnlyFacilities || '',
            private_hire: c.results?.leisureFacilities.privateHire || '',
            follow_up: c.results?.meta?.followUpAction || '',
          }));
          const headers = Object.keys(rows[0] || { id: '', phone: '', property: '', status: '', halal: '', alcohol: '', modest: '', women_only: '', private_hire: '', follow_up: '' });
          const csv = [headers.join(',')].concat(rows.map(r => headers.map(h => String((r as any)[h]).replace(/,/g,' ')).join(','))).join('\n');
          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'filtered-calls.csv';
          a.click();
          URL.revokeObjectURL(url);
        }}>Export Filtered CSV</button>
      </div>

      {isLoading && calls.length > 0 && (
        <div className="updating-indicator">
          <div className="mini-spinner"></div>
          <span>Updating...</span>
        </div>
      )}

      <style>{`
        .call-history {
          margin-top: 2rem;
        }

        .history-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .history-header h2 {
          margin: 0;
          color: var(--hb-slate);
          font-size: 1.75rem;
          font-weight: 700;
        }

        .call-count {
          background: var(--hb-bg);
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.875rem;
          color: var(--hb-slate);
          font-weight: 600;
          border: 1px solid var(--hb-border);
          box-shadow: var(--hb-shadow);
        }

        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
          align-items: center;
          background: var(--hb-bg-secondary);
          padding: 1.25rem;
          border-radius: var(--hb-radius);
          border: 1px solid var(--hb-border);
          box-shadow: var(--hb-shadow);
        }

        .filter-input {
          flex: 1;
          min-width: 200px;
          padding: 0.75rem 1rem;
          border: 2px solid var(--hb-border);
          border-radius: var(--hb-radius);
          font-size: 0.9rem;
          background: var(--hb-bg-secondary);
          transition: all 0.2s;
        }

        .filter-input:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .filter-select {
          padding: 0.6rem 0.75rem;
          border: 2px solid var(--hb-border);
          border-radius: var(--hb-radius);
          background: var(--hb-bg-secondary);
          font-size: 0.9rem;
          transition: all 0.2s;
        }

        .filter-select:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
          background: white;
        }

        .clear-btn {
          background: var(--hb-muted);
          color: white;
          border: none;
          padding: 0.6rem 1rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .clear-btn:hover {
          background: #4a5568;
          transform: translateY(-1px);
        }

        .calls-list {
          display: flex;
          flex-direction: column;
        }

        .call-history-loading {
          text-align: center;
          padding: 3rem;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        .call-history-empty {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          padding: 3rem;
          text-align: center;
        }

        .empty-state h3 {
          margin: 0 0 1rem 0;
          color: #6c757d;
        }

        .empty-state p {
          margin: 0;
          color: #6c757d;
          font-size: 1rem;
        }

        .updating-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 1rem;
          background: #e7f3ff;
          border-radius: 4px;
          margin-top: 1rem;
          font-size: 0.875rem;
          color: #007bff;
        }

        .mini-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid #e7f3ff;
          border-top: 2px solid #007bff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .batch-group {
          border: 2px solid var(--hb-border);
          border-radius: var(--hb-radius-lg);
          padding: 0.75rem;
          margin-bottom: 1.25rem;
          background: var(--hb-bg-secondary);
          box-shadow: var(--hb-shadow);
        }

        .batch-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.5rem 0.75rem;
          background: var(--hb-bg);
          border-radius: var(--hb-radius);
          margin-bottom: 0.5rem;
        }

        .batch-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .batch-title .name {
          font-weight: 600;
          color: var(--hb-slate);
          font-size: 1rem;
        }

        .batch-title .count {
          background: var(--hb-sea);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .batch-toggle {
          background: var(--hb-muted);
          color: white;
          border: none;
          padding: 0.5rem 0.75rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          transition: all 0.2s;
        }

        .batch-toggle:hover {
          background: #4a5568;
          transform: translateY(-1px);
        }

        .batch-calls {
          margin-top: 0.75rem;
        }

        .pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 1.5rem 0;
          padding: 1rem;
          background: var(--hb-bg-secondary);
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          box-shadow: var(--hb-shadow);
        }

        .pagination-btn {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .pagination-btn:hover:not(:disabled) {
          background: var(--hb-sea-dark);
          transform: translateY(-1px);
        }

        .pagination-btn:disabled {
          background: var(--hb-muted);
          cursor: not-allowed;
          transform: none;
          opacity: 0.6;
        }

        .pagination-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .page-indicator {
          font-weight: 600;
          color: var(--hb-slate);
          font-size: 0.9rem;
        }

        .item-count {
          color: var(--hb-muted);
          font-size: 0.8rem;
        }

        .export-row {
          display: flex;
          justify-content: flex-end;
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--hb-border-light);
        }

        .export-btn {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 600;
          font-size: 0.9rem;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .export-btn:hover {
          background: var(--hb-sea-dark);
          transform: translateY(-1px);
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default CallHistoryList;



